import * as React from 'react';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { SearchInput } from './SearchHeaderBlocks/SearchInput';
import { PopularProducts } from './SearchHeaderBlocks/PopularProducts';
import { PopularSearches } from './SearchHeaderBlocks/PopularSearches';
import { SearchSuggestions } from './HeaderSearchResults/SearchSuggestions';
import { SearchCategories } from './HeaderSearchResults/SearchCategories';
import { SearchColors } from './HeaderSearchResults/SearchColors';
import { HeaderSearchResults } from './HeaderSearchResults/HeaderSearchResults';
import { CategoryItem } from '../../../types/search';
import shopify from 'frontend/js/shopify';
import { IGraphQlProduct } from '@src/types/product';

interface SearchHeaderProps {
  settings?: {
    placeholder_text?: string;
    show_popular_products?: boolean;
    popular_products_title?: string;
    show_popular_searches?: boolean;
    popular_searches_title?: string;
    popular_searches_list?: string;
    no_products_fallback_url?: string;
    show_search_suggestions?: boolean;
    search_suggestions_title?: string;
    show_category_filter?: boolean;
    category_filter_title?: string;
    show_color_filter?: boolean;
    color_filter_title?: string;
    search_results_event_name?: string;
    search_state_event_name?: string;
    search_clear_event_name?: string;
    [key: string]: any;
    tile_type?: string;
  };
  className?: string;
}

export const SearchHeader: React.FC<SearchHeaderProps> = ({ settings, className = '' }) => {
  const { popular_products } = settings || {};

  const { isAboveTablet } = useResponsiveBreakpoints();

  const [searchQuery, setSearchQuery] = useState('');
  const [hasSearchResults, setHasSearchResults] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [searchCategories, setSearchCategories] = useState([]);
  const [searchColors, setSearchColors] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const [seeAllProductsUrl, setSeeAllProductsUrl] = useState<string>('');
  const [reFocus, setReFocus] = useState(false);
  const [popularProducts, setPopularProducts] = useState<IGraphQlProduct[]>([]);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<any>(null);

  // console.log('SearchHeader settings:', settings.popular_products);

  // Memoized helper functions to prevent re-renders
  const areProductsSame = useCallback((products1, products2) => {
    if (products1.length !== products2.length) return false;

    const ids1 = products1.map((p) => p.id).sort();
    const ids2 = products2.map((p) => p.id).sort();

    return ids1.every((id, index) => id === ids2[index]);
  }, []);

  const areArraysSame = useCallback((arr1, arr2) => {
    return JSON.stringify(arr1) === JSON.stringify(arr2);
  }, []);

  // Listen for search results from the search provider
  useEffect(() => {
    const handleSearchResults = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { type, products, totalResults, query, action, categories, colors, suggestions, currentFilters } =
        customEvent.detail;

      // Only handle results if a search is in progress
      if (type === 'header-search') {
        console.log('Received search results from provider:', {
          products,
          totalResults,
          query,
          categories,
          colors,
          suggestions
        });

        // Clear any existing timeout
        if (searchTimeout) {
          clearTimeout(searchTimeout);
          setSearchTimeout(null);
        }

        // Check if products are the same as current products to avoid unnecessary updates
        const incomingProducts = products || [];
        const currentProducts = searchResults || [];

        // Only update products if they're different
        if (!areProductsSame(incomingProducts, currentProducts)) {
          console.log('SearchHeader: Setting new products, count:', incomingProducts.length);
          setSearchResults(products || []);
          setHasSearchResults(products && products.length > 0);
        } else {
          console.log('SearchHeader: Products are the same, skipping update');
        }

        setTotalResults(totalResults);
        setIsSearching(false); // Only set to false after results are handled
        setSearchError('');

        // Update categories, colors, and suggestions from the search provider only if different
        const incomingCategories = categories || [];
        const incomingColors = colors || [];
        const incomingSuggestions = suggestions || [];

        if (!areArraysSame(incomingCategories, searchCategories)) {
          setSearchCategories(categories || []);
        }

        if (!areArraysSame(incomingColors, searchColors)) {
          setSearchColors(colors || []);
        }

        if (!areArraysSame(incomingSuggestions, searchSuggestions)) {
          setSearchSuggestions(suggestions || []);
        }
        window.dispatchEvent(
          new CustomEvent('searchrequest-categories-complete', {
            detail: { categories: categories || [] }
          })
        );
        window.dispatchEvent(
          new CustomEvent('searchrequest-colors-complete', {
            detail: { colors: colors || [] }
          })
        );
        window.dispatchEvent(
          new CustomEvent('searchrequest-suggestions-complete', {
            detail: { suggestions: suggestions || [] }
          })
        );

        // Restore focus to search input after results are received
        setTimeout(() => {
          if (reFocus && searchInputRef.current && searchInputRef.current.focus) {
            searchInputRef.current.focus();
          }
        }, 100);
        setReFocus(false);

        // Fire custom event for other blocks to listen to
        const searchCompleteEvent = new CustomEvent(
          settings?.block_settings?.search_results_event_name || 'search-results-ready',
          {
            detail: {
              query: query,
              results: products || [],
              suggestions: suggestions || [],
              categories: categories || [],
              colors: colors || [],
              totalResults: totalResults || 0,
              timestamp: new Date().toISOString()
            }
          }
        );
        window.dispatchEvent(searchCompleteEvent);

        // Fire search state event
        const searchToggleEvent = new CustomEvent(settings?.search_state_event_name || 'search-state-changed', {
          detail: {
            hasResults:
              (products && products.length > 0) ||
              ((!products || products.length === 0) && currentFilters && currentFilters.length > 0),
            query: query,
            resultCount: totalResults || 0
          }
        });
        window.dispatchEvent(searchToggleEvent);
      } else {
      }
    };

    // Listen for search errors
    const handleSearchError = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { message, query } = customEvent.detail;

      // Clear any existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }

      setIsSearching(false);
      setSearchError(message || 'An error occurred while searching');
    };

    // Add event listeners for search results and errors
    window.addEventListener('searchrequest-products-complete', handleSearchResults);
    window.addEventListener('searchrequest-error', handleSearchError);

    // Cleanup
    return () => {
      window.removeEventListener('searchrequest-products-complete', handleSearchResults);
      window.removeEventListener('searchrequest-error', handleSearchError);

      // Clear any pending timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [
    settings,
    searchTimeout,
    isSearching,
    searchResults,
    searchCategories,
    searchColors,
    searchSuggestions,
    areProductsSame,
    areArraysSame
  ]);

  // Memoized values to prevent unnecessary re-renders
  const blockSettings = useMemo(() => settings?.block_settings || {}, [settings?.block_settings]);

  const popularSearches = useMemo(
    () => blockSettings.popular_searches_list?.split(',').map((s) => s.trim()) || [],
    [blockSettings.popular_searches_list]
  );

  // Separate function to perform the actual search
  const performSearch = useCallback(
    (query: string) => {
      // Set searching state only when we actually perform the search
      setIsSearching(true);
      setIsTyping(false);

      // Clear any existing search timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Fire search request to the search provider
      const searchRequestEvent = new CustomEvent('searchrequest', {
        detail: {
          query: query,
          type: 'header-search',
          page: 1,
          productsPerPage: settings?.products_per_page || 24,
          sort: settings?.default_sort || 'RELEVANCE',
          direction: settings?.default_sort_direction || 'DESC',
          filters: []
        }
      });

      window.dispatchEvent(searchRequestEvent);

      // Set a timeout to handle cases where the search provider doesn't respond
      const timeout = setTimeout(() => {
        console.warn('Search request timed out for query:', query);
        console.warn('No response received from search provider within 10 seconds');
        console.warn(
          'Make sure a search provider (like ShopifySearchProvider) is mounted and listening for searchrequest events'
        );
        setIsSearching(false);
        setSearchError('Search request timed out. Please try again.');
      }, 10000); // 10 second timeout

      setSearchTimeout(timeout);
    },
    [searchTimeout, settings?.products_per_page, settings?.default_sort, settings?.default_sort_direction]
  );

  // Real-time search (for typing and showing header results)
  const handleSearch = useCallback(
    (query: string) => {
      setSearchQuery(query);
      setSeeAllProductsUrl(`/search?q=${encodeURIComponent(query)}`);
      setIsTyping(true);
      setReFocus(true);

      // // Clear any existing debounce timeout
      // if (debounceTimeoutRef.current) {
      //   clearTimeout(debounceTimeoutRef.current);
      //   debounceTimeoutRef.current = null;
      // }

      // If query is empty, reset to initial state immediately
      if (query.trim().length === 0) {
        console.log('SearchHeader: Resetting search state due to empty query');
        setHasSearchResults(false);
        setSearchResults([]);
        setSearchSuggestions([]);
        setSearchCategories([]);
        setSearchColors([]);
        setIsSearching(false);
        setSearchError('');

        // Fire event for cleared search state
        const searchToggleEvent = new CustomEvent(blockSettings.search_state_event_name || 'search-state-changed', {
          detail: {
            hasResults: false,
            query: '',
            resultCount: 0
          }
        });
        window.dispatchEvent(searchToggleEvent);

        // Fire specific clear event for actions to listen to
        const searchClearEvent = new CustomEvent(blockSettings.search_clear_event_name || 'search-cleared', {
          detail: {
            timestamp: new Date().toISOString(),
            previousQuery: searchQuery
          }
        });
        window.dispatchEvent(searchClearEvent);

        return;
      }

      // Clear any previous search error
      setSearchError('');

      performSearch(query);
    },
    [blockSettings.search_state_event_name, blockSettings.search_clear_event_name, searchQuery, performSearch]
  );

  const handleSuggestionClick = useCallback((suggestion: string) => {
    window.location.href = `/search?q=${encodeURIComponent(suggestion)}`;
  }, []);

  const handleCategoryClick = useCallback((category: CategoryItem) => {
    // Redirect to the collection page using the handle or URL
    const collectionUrl = category.url || `/collections/${category.handle}`;
    window.location.href = collectionUrl;
  }, []);

  const handleColorClick = useCallback((color: string) => {
    window.location.href = `/search?q=${encodeURIComponent(color)}`;
  }, []);

  const handlePopularSearchClick = useCallback((collection: any) => {
    window.location.href = `/collections/${collection.handle}`;
  }, []);

  const handleViewMoreProducts = useCallback(() => {
    // Navigate to full search results page
    window.location.href = `/search?q=${encodeURIComponent(searchQuery)}`;
  }, [searchQuery]);

  const handleSeeAllProducts = useCallback(() => {
    // Navigate to full search results page
    window.location.href = seeAllProductsUrl || `/search?q=${encodeURIComponent(searchQuery)}`;
  }, [seeAllProductsUrl, searchQuery]);

  // Stable settings object for product rendering - minimal dependencies
  const stableProductSettings = useMemo(
    () => ({
      products: searchResults,
      totalResults,
      settings: settings, // Pass settings directly instead of through handler
      hasResults: searchResults.length > 0 // Calculate hasResults from products directly
    }),
    [searchResults, totalResults, settings]
  );

  // Memoized product grid that only re-renders when products actually change
  const productGrid = useMemo(() => {
    if (!stableProductSettings.hasResults) {
      return null;
    }

    // Create a completely isolated handler that doesn't depend on external state
    const handleSeeAllProductsStable = () => {
      // Get current values at click time, not render time
      const currentQuery = searchQuery;
      const currentUrl = seeAllProductsUrl || `/search?q=${encodeURIComponent(currentQuery)}`;
      window.location.href = currentUrl;
    };

    return (
      <div className='flex w-full relative'>
        <HeaderSearchResults
          products={stableProductSettings.products}
          settings={stableProductSettings.settings}
          totalResults={stableProductSettings.totalResults}
          onSeeAllProducts={handleSeeAllProductsStable}
        />
      </div>
    );
  }, [stableProductSettings]);

  // Memoized filter sidebar that only re-renders when suggestions/categories/colors change
  const filterSidebar = useMemo(() => {
    return (
      <div className='w-64 space-y-6 flex-shrink-0 hidden'>
        {/* Search Suggestions */}
        {blockSettings.show_search_suggestions !== false && searchSuggestions.length > 0 && (
          <SearchSuggestions
            title={blockSettings.search_suggestions_title || 'Search suggestions'}
            suggestions={searchSuggestions}
            onSuggestionClick={handleSuggestionClick}
          />
        )}

        {/* Categories Filter */}
        {blockSettings.show_category_filter !== false && searchCategories.length > 0 && (
          <SearchCategories
            title={blockSettings.category_filter_title || 'Categories'}
            categories={searchCategories}
            onCategoryClick={handleCategoryClick}
          />
        )}
        {/* Debug: Show current searchCategories state */}
        {process.env.NODE_ENV === 'development' && (
          <div style={{ fontSize: '12px', color: 'red', padding: '10px', background: '#f0f0f0' }}>
            DEBUG - searchCategories length: {searchCategories.length}
            <br />
            Categories: {JSON.stringify(searchCategories, null, 2)}
          </div>
        )}

        {/* Colors Filter */}
        {blockSettings.show_color_filter !== false && searchColors.length > 0 && (
          <SearchColors
            title={blockSettings.color_filter_title || 'Colours'}
            colors={searchColors}
            onColorClick={handleColorClick}
          />
        )}
      </div>
    );
  }, [
    blockSettings,
    searchSuggestions,
    searchCategories,
    searchColors,
    handleSuggestionClick,
    handleCategoryClick,
    handleColorClick
  ]);

  // Memoized popular content that only re-renders when settings change
  const popularContent = useMemo(() => {
    return (
      <div className='space-y-6'>
        {searchQuery && !isTyping && !isSearching && (
          <p className='mb-6 pb-6 border-b border-b-[##C2C2C2] b5--bold'>
            No products found for search term '{searchQuery}'
          </p>
        )}

        <PopularProducts
          title={blockSettings.popular_products_title || 'Popular Products'}
          onViewMore={handleViewMoreProducts}
          products={popularProducts}
          settings={settings.block_settings}
        />

        <div className='flex flex-col-reverse md:flex-row w-full md:items-end justify-between'>
          <PopularSearches
            title={blockSettings.popular_searches_title || 'Popular searches'}
            onSearchClick={handlePopularSearchClick}
            searches={popularSearches}
          />
          <a
            href={blockSettings.no_products_fallback_url || '/collections/all'}
            className='button button-outline w-full md:w-auto mb-4 md:mb-0 !px-6'
          >
            View more products
          </a>
        </div>
      </div>
    );
  }, [
    searchQuery,
    isTyping,
    isSearching,
    blockSettings,
    handleViewMoreProducts,
    popularProducts,
    settings,
    handlePopularSearchClick,
    popularSearches
  ]);

  const handleFilterApplied = (event: Event) => {
    const { filterId, applying, filterType, input, value } = (event as CustomEvent).detail || {};

    setSeeAllProductsUrl((prev) => {
      if (filterType === 'price_range') {
        const priceData = JSON.parse(input);
        const { min, max } = priceData.price;

        let updatedUrl = prev;

        // Handle price.min parameter
        if (updatedUrl.includes('price.min=')) {
          // Replace existing price.min value
          updatedUrl = updatedUrl.replace(/price\.min=[\d.]+/, `price.min=${min}`);
        } else {
          // Add new price.min parameter
          updatedUrl += updatedUrl.includes('?') ? `&price.min=${min}` : `?price.min=${min}`;
        }

        // Handle price.max parameter
        if (updatedUrl.includes('price.max=')) {
          // Replace existing price.max value
          updatedUrl = updatedUrl.replace(/price\.max=[\d.]+/, `price.max=${max}`);
        } else {
          // Add new price.max parameter
          updatedUrl += `&price.max=${max}`;
        }

        return updatedUrl;
      } else {
        const param = shopify.generateProductFilterUrlParam(filterId, value);

        if (applying) {
          return (prev += `&${param}`);
        } else {
          return (prev = prev.replace(`&${param}`, ''));
        }
      }
    });
  };

  const handleClickClose = () => {
    // Find the event_id from the parent section settings
    const eventId = settings?.section?.event_id || 'header-search';
    window.dispatchEvent(new CustomEvent(`${eventId}-hide`));
  };

  useEffect(() => {
    window.addEventListener('searchrequest-filter-applied', handleFilterApplied);

    return () => {
      window.removeEventListener('searchrequest-filter-applied', handleFilterApplied);
    };
  }, []);

  // Fetch popular products
  useEffect(() => {
    if (!popular_products || popular_products.length === 0) return;

    // Fetch popular products from the settings
    const fetchPopularProducts = async () => {
      try {
        const products = await shopify.getShopifyProductList(popular_products.map((product) => product.id));
        setPopularProducts(products);
      } catch (error) {
        console.error('Error fetching popular products:', error);
      }
    };

    fetchPopularProducts();
  }, [settings]);

  // Focus the search input on mount
  useEffect(() => {
    if (!searchInputRef.current) return;

    const focusInput = () => {
      searchInputRef.current.focus();
    };

    const eventId = settings?.section?.event_id || 'header-search';
    window.addEventListener(`${eventId}-toggle`, focusInput);

    return () => window.removeEventListener(`${eventId}-toggle`, focusInput);
  }, [searchInputRef, settings]);

  return (
    <div className={`w-full p-4 lg:p-6 bg-white ${className}`}>
      {/* Search Input */}
      <div className='mb-6 relative'>
        <div className='flex items-center justify-between gap-x-8'>
          <SearchInput
            ref={searchInputRef}
            placeholder={blockSettings.placeholder_text || 'Search'}
            value={searchQuery}
            onSearch={handleSearch}
            isLoading={isSearching}
            maintainFocus={true}
          />

          {isAboveTablet && (
            <button
              type='button'
              onClick={handleClickClose}
            >
              <i className='icon icon-close text-[24px]'></i>
            </button>
          )}
        </div>
        {searchError && <div className='mt-2 text-red-600 text-sm'>{searchError}</div>}
      </div>

      {/* Content - switches based on search state */}
      {!stableProductSettings.hasResults ? (
        // Initial state - show popular products and searches
        popularContent
      ) : (
        // Search results state - show search results and filters
        <div className='flex gap-8 w-full '>
          {/* Left sidebar - Filters */}
          {filterSidebar}

          {/* Right content - Search results */}
          {productGrid}
        </div>
      )}
    </div>
  );
};
